import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import './SubscriptionSuccess.css';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently, user } = useAuth0();
  const hasProcessed = useRef(false);
  
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your subscription...');
  const [countdown, setCountdown] = useState(10);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const sessionId = searchParams.get('session_id');
  const plan = searchParams.get('plan') || 'basic';
  const billingCycle = searchParams.get('billing_cycle') || 'monthly';

  // Format plan name for display
  const getPlanDisplayName = (planId) => {
    const names = {
      basic: 'Basic Plan',
      standard: 'Standard Plan',
      starter_studio: 'Starter Studio',
      growth_studio: 'Growth Studio'
    };
    return names[planId] || 'Subscription Plan';
  };

  // Get plan features
  const getPlanFeatures = (planId) => {
    const features = {
      basic: [
        'Unlimited Appointment Scheduling',
        'Unlimited Basic Client Profiles',
        'Client Management',
        'Digital Consent Forms',
        'Customizable Consent Forms',
        'Email Support'
      ],
      standard: [
      'Everything in Basic',
      'Advanced Client Profiles (Tattoo History, Allergies, Style Preferences)',
      'Appointment Reminders (Email)',
      'Automated Aftercare Emails',
      'Booking Enhancements',
      'Low Stock Alerts',
      'Inventory Management',
      'Low Stock Alerts',
      ],
      starter_studio: [
      'All Basic Features (per seat)',
      'Basic Client Profiles',
      'Shared Client Profiles',
      'Invite & Manage Artists',
      'Role-Based Permissions',
      'Shared Form Templates',
      'Cross-Artist Calendar View',
      'Unified Billing',
      'Studio-wide Inventory Management',
      'Three additional users included',
      'Additional seats at $25/month',
      ],
      growth_studio: [
      'Everything in Starter Studio',
      'Advanced Client Profiles',
      'Studio Dashboard',
      'Automated Appointment Reminders (Email)',
      'Automated Aftercare Instructions',
      'Studio-wide Inventory Management',
      'Three additional users included',
      'Additional seats $40/month',
      ]
    };
    return features[planId] || ['Premium features included'];
  };

  // Check if it's a studio plan
  const isStudioPlan = (planId) => {
    return ['starter_studio', 'growth_studio'].includes(planId);
  };

  // Process the subscription
  useEffect(() => {
    if (hasProcessed.current || !user) return;
    hasProcessed.current = true;
    if (sessionId) {
      processSubscription();
    } else {
      // No session ID - might be direct navigation
      setStatus('success');
      setMessage('Welcome to your subscription!');
      startCountdown();
    }
  }, [user, sessionId]);

  const processSubscription = async () => {
    try {
      setStatus('processing');
      setMessage('Verifying your subscription with Stripe...');

      const token = await getAccessTokenSilently({ cacheMode: 'off' });

      // Call the subscription success endpoint
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/subscription/success/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId
        })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setStatus('success');
        setMessage('Your subscription has been activated!');
        setSubscriptionInfo(data);
        startCountdown();
      } else {
        // Even if verification had issues, payment likely went through
        setStatus('success');
        setMessage('Your payment was processed. Setting up your account...');
        startCountdown();
      }
    } catch (error) {
      console.error('Error processing subscription:', error);
      // Don't show error - payment probably went through
      setStatus('success');
      setMessage('Your payment was processed. Finalizing setup...');
      startCountdown();
    }
  };

  const startCountdown = () => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          checkProfileAndRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  };

  const checkProfileAndRedirect = async () => {
    try {
      const token = await getAccessTokenSilently({ cacheMode: 'off' });

      // Check both user and shop profiles
      const [userResponse, shopResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/user/profile/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/shop/profile/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (userResponse.ok && shopResponse.ok) {
        const userData = await userResponse.json();
        const shopData = await shopResponse.json();

        const needsOnboarding = !userData.first_name?.trim() || 
                               !userData.last_name?.trim() || 
                               !shopData.name?.trim() ||
                               !shopData.address?.trim();

        if (needsOnboarding) {
          navigate('/onboarding', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (error) {
      console.error('Error checking profile:', error);
      navigate('/dashboard', { replace: true });
    }
  };

  const currentPlan = subscriptionInfo?.plan_id || plan;
  const planFeatures = getPlanFeatures(currentPlan);

  // Show loading state
  if (status === 'processing') {
    return (
      <div className="subscription-success-container">
        <div className="success-icon" style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}>
          ⏳
        </div>
        <h1>Processing Your Subscription</h1>
        <p>{message}</p>
        <div style={{ margin: '20px 0' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #e0e0e0',
            borderTopColor: '#4CAF50',
            borderRadius: '50%',
            margin: '0 auto',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="subscription-success-container">
      {/* Success Icon */}
      <div className="success-icon">✓</div>

      {/* Main Message */}
      <h1>Welcome to {getPlanDisplayName(currentPlan)}!</h1>
      <p>{message}</p>

      {/* Next Steps / Features */}
      <div className="next-steps">
        <h2>Your Plan Includes:</h2>
        <ul>
          {planFeatures.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
      </div>

      {/* Studio Notice */}
      {isStudioPlan(currentPlan) && (
        <div className="next-steps" style={{ backgroundColor: '#e3f2fd', borderLeft: '4px solid #1976d2' }}>
          <h2>🎨 Studio Management Ready!</h2>
          <p style={{ fontSize: '16px', marginBottom: 0 }}>
            You can now invite team members and manage your studio. 
            Visit the Studio Management section to get started.
          </p>
        </div>
      )}

      {/* Redirect Notice */}
      <div style={{ 
        backgroundColor: '#fff3e0', 
        padding: '15px', 
        borderRadius: '8px', 
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px'
      }}>
        <div style={{ 
          width: '20px', 
          height: '20px', 
          border: '2px solid #ff9800',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <div>
          <strong>Setting up your account...</strong>
          <br />
          <span style={{ color: '#666', fontSize: '14px' }}>
            Redirecting in {countdown} seconds
          </span>
        </div>
      </div>

      {/* Continue Button */}
      <button 
        className="continue-button"
        onClick={() => navigate('/dashboard')}
      >
        Go to Dashboard
      </button>

      {isStudioPlan(currentPlan) && (
        <button 
          className="continue-button"
          style={{ 
            backgroundColor: 'white', 
            color: '#4CAF50', 
            border: '2px solid #4CAF50',
            marginLeft: '10px'
          }}
          onClick={() => navigate('/studio-management')}
        >
          Manage Studio
        </button>
      )}

      {/* Support Note */}
      <div className="support-note">
        <p><strong>Need help getting started?</strong></p>
        <p>
          Check out our <a href="/faq">FAQ</a> or <a href="/support">contact support</a> if you have any questions.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;